import { useEffect, useMemo, useState } from 'react';
import { computeAccrued, planDurationSec } from '../../lib/format';

// Тикающий локальный pending-счётчик на клиенте — не долбим RPC, раз в 1с пересчёт формулы.
// On-chain значение (pendingProfitAll) используется как reference point:
// если drift > 1% — подменяем в следующем тике (эффект "snap").
export function useLivePending(deposits, plans, onChainTotal) {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, []);

  const local = useMemo(() => {
    if (!deposits?.length || !plans?.length) return 0n;
    let total = 0n;
    for (const d of deposits) {
      if (!d.active) continue;
      const plan = plans[Number(d.planId)];
      if (!plan) continue;
      total += computeAccrued({
        amount: d.amount,
        dailyBps: plan.dailyBps,
        startedAt: d.startedAt,
        lastClaimAt: d.lastClaimAt,
        durationSec: planDurationSec(plan),
        now,
      });
    }
    return total;
  }, [deposits, plans, now]);

  // Если on-chain сильно отличается (после tx), доверяем ему и не прыгаем локально.
  const display = useMemo(() => {
    if (onChainTotal === undefined || onChainTotal === null) return local;
    if (local === 0n && onChainTotal === 0n) return 0n;
    // local обычно >= onChain (тикает между блоками), но не ниже
    return local > onChainTotal ? local : onChainTotal;
  }, [local, onChainTotal]);

  return display;
}

// Per-deposit вариант — для карточки депозита
export function useLiveAccrued(deposit, plan) {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    if (!deposit?.active) return undefined;
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, [deposit?.active]);

  return useMemo(() => {
    if (!deposit?.active || !plan) return 0n;
    return computeAccrued({
      amount: deposit.amount,
      dailyBps: plan.dailyBps,
      startedAt: deposit.startedAt,
      lastClaimAt: deposit.lastClaimAt,
      durationSec: planDurationSec(plan),
      now,
    });
  }, [deposit, plan, now]);
}
